<script setup lang="ts">
import { computed } from 'vue'
import type { WidgetDensity } from '@/composables/useCustomDashboardWidget'

export type WidgetLayoutVariant =
  | 'centered'
  | 'split-header'
  | 'dot-with-subtitle'
  | 'dot-title-only'

const props = withDefaults(
  defineProps<{
    variant: WidgetLayoutVariant
    title: string
    subtitle?: string
    accentColor?: string
    editMode: boolean
    contentClass?: string
    density?: WidgetDensity
  }>(),
  {
    subtitle: '',
    accentColor: '#123add',
    contentClass: '',
    density: 'comfortable',
  },
)

const centeredHeaderClass = computed(() => {
  if (props.density === 'dense') return 'shrink-0 px-1.5 pb-0 pt-1.5 text-center'
  if (props.density === 'compact') return 'shrink-0 px-2 pb-0.5 pt-2 text-center'
  return 'shrink-0 px-4 pb-1 pt-4 text-center'
})

const centeredTitleClass = computed(() => {
  if (props.density === 'dense') return 'min-w-0 truncate font-satoshi text-xs font-bold text-black'
  if (props.density === 'compact') return 'min-w-0 truncate font-satoshi text-sm font-bold text-black'
  return 'font-satoshi text-base font-bold text-black'
})

const showCenteredSubtitle = computed(() => Boolean(props.subtitle))

const centeredSubtitleGapClass = computed(() => {
  if (props.density === 'dense') return 'mt-0.5'
  if (props.density === 'compact') return 'mt-1'
  return 'mt-2'
})

const centeredSubtitleClass = computed(() => {
  const base = 'max-w-full truncate font-medium uppercase text-gray-500'
  if (props.density === 'dense') {
    return `${base} text-3xs tracking-normal ${centeredSubtitleGapClass.value}`
  }
  if (props.density === 'compact') {
    return `${base} text-2xs tracking-wide ${centeredSubtitleGapClass.value}`
  }
  return `${base} text-2xs tracking-widest ${centeredSubtitleGapClass.value}`
})

const splitHeaderClass = computed(() => {
  const editPadding = props.editMode ? 'pr-16' : ''
  if (props.density === 'dense') return `shrink-0 border-b border-stroke px-1.5 py-1 ${editPadding}`
  if (props.density === 'compact') return `shrink-0 border-b border-stroke px-2 py-1.5 ${editPadding}`
  return `shrink-0 border-b border-stroke px-4 py-2 ${editPadding}`
})

const splitTitleClass = computed(() => {
  if (props.density === 'dense') {
    return 'min-w-0 truncate font-satoshi text-xs font-bold text-black'
  }
  if (props.density === 'compact') {
    return 'min-w-0 truncate font-satoshi text-xs font-bold text-black'
  }
  return 'min-w-0 truncate font-satoshi text-sm font-bold text-black'
})

const splitHeaderRowClass = computed(() => {
  if (props.density === 'dense') return 'flex min-w-0 items-center justify-between gap-1'
  if (props.density === 'compact') return 'flex min-w-0 items-center justify-between gap-2'
  return 'flex min-w-0 items-center justify-between gap-3'
})

const showSplitSubtitle = computed(() => Boolean(props.subtitle))

const splitSubtitleClass = computed(() => {
  const base = 'shrink-0 truncate text-right font-medium uppercase text-gray-500'
  if (props.density === 'dense') {
    return `${base} max-w-[40%] text-3xs tracking-normal`
  }
  if (props.density === 'compact') {
    return `${base} max-w-[42%] text-2xs tracking-wide`
  }
  return `${base} max-w-[45%] text-2xs tracking-widest`
})
</script>

<template>
  <!-- centered: indicator, gauge -->
  <div
    v-if="variant === 'centered'"
    class="drag-handle flex flex-1 cursor-move flex-col"
  >
    <div :class="centeredHeaderClass">
      <h3 :class="centeredTitleClass">{{ title }}</h3>
      <p v-if="showCenteredSubtitle" :class="centeredSubtitleClass">
        {{ subtitle }}
      </p>
    </div>
    <div class="flex min-h-0 flex-1 flex-col" :class="contentClass">
      <slot />
    </div>
  </div>

  <!-- split-header: status-panel, time-series, open-alarms -->
  <div
    v-else-if="variant === 'split-header'"
    class="drag-handle flex min-h-0 flex-1 cursor-move flex-col"
  >
    <div :class="splitHeaderClass">
      <div :class="splitHeaderRowClass">
        <h3 :class="splitTitleClass">
          {{ title }}
        </h3>
        <p
          v-if="showSplitSubtitle"
          :class="splitSubtitleClass"
        >
          {{ subtitle }}
        </p>
      </div>
    </div>
    <div class="flex min-h-0 flex-1 flex-col" :class="contentClass">
      <slot />
    </div>
  </div>

  <!-- dot-with-subtitle: pie-chart -->
  <div
    v-else-if="variant === 'dot-with-subtitle'"
    class="drag-handle flex min-h-0 flex-1 cursor-move flex-col"
  >
    <div
      class="shrink-0 border-b border-stroke px-4 py-2"
      :class="editMode ? 'pr-16' : ''"
    >
      <div class="flex min-w-0 items-start gap-2">
        <span
          class="mt-1 inline-block h-2 w-2 shrink-0 rounded-none"
          :style="{ backgroundColor: accentColor }"
          aria-hidden="true"
        />
        <div class="min-w-0">
          <h3 class="font-satoshi text-sm font-bold text-black">{{ title }}</h3>
          <p
            v-if="subtitle"
            class="mt-1 text-2xs font-medium uppercase tracking-widest text-gray-500"
          >
            {{ subtitle }}
          </p>
        </div>
      </div>
    </div>
    <div class="flex min-h-0 flex-1 overflow-hidden" :class="contentClass">
      <slot />
    </div>
  </div>

  <!-- dot-title-only: bar-chart -->
  <div
    v-else-if="variant === 'dot-title-only'"
    class="drag-handle flex min-h-0 flex-1 cursor-move flex-col"
  >
    <div
      class="shrink-0 border-b border-stroke px-4 py-2"
      :class="editMode ? 'pr-16' : ''"
    >
      <div class="flex min-w-0 items-center gap-2">
        <span
          class="mt-0.5 inline-block h-2 w-2 shrink-0 rounded-none"
          :style="{ backgroundColor: accentColor }"
          aria-hidden="true"
        />
        <h3 class="min-w-0 truncate font-satoshi text-sm font-bold text-black">
          {{ title }}
        </h3>
      </div>
    </div>
    <div class="flex min-h-0 flex-1 flex-col" :class="contentClass">
      <slot />
    </div>
  </div>
</template>
